import { Archive, ArchiveRestore } from 'lucide-react';

/**
 * Archive and unarchive, told apart at a glance.
 *
 * Two problems the client hit at once. The same box icon was doing both jobs,
 * so the button that puts a record back looked exactly like the one that takes
 * it away — on a row where both can appear, that is a trap. And neither
 * carried any colour, so nothing said which direction you were going.
 *
 * So: a box with a down arrow in red for putting something away, a box with an
 * up arrow in green for bringing it back. Defined once here rather than at the
 * forty-odd call sites, so every list agrees and a later change is one edit.
 *
 * The colour is on the icon rather than the button: these sit inside ghost and
 * outline buttons whose own colour has other jobs to do, and a whole red
 * button reads as "delete", which is the thing archiving exists not to be.
 */
export function ArchiveIcon({ className }: { className?: string }) {
  return <Archive aria-hidden className={className ?? 'text-danger-600'} />;
}

export function UnarchiveIcon({ className }: { className?: string }) {
  return <ArchiveRestore aria-hidden className={className ?? 'text-signal-600'} />;
}

/**
 * The badge that says a row is archived.
 *
 * With "Show archived" ticked, archived rows sit among the live ones. Without
 * a marker the only difference is which button the row offers, which is not
 * something anybody should have to read a row by.
 */
export function ArchivedPill({ className }: { className?: string }) {
  return (
    <span
      className={
        className ??
        'inline-flex shrink-0 items-center rounded-full bg-danger-50 px-2 py-0.5 text-2xs font-semibold uppercase tracking-[0.04em] text-danger-600'
      }
    >
      Archived
    </span>
  );
}
